/**
 * The AI's note on a Macro section — or, with `kind="overall"`, across them
 * (`docs/plans/macro-commentary.md`).
 *
 * A collapsible block at the top of the section: 3–5 sentences and, when the
 * numbers allow, one line on when this state happened before and what followed.
 * Stamped `AI · as of <date> · ↻` beside QueVee's mark — **the visible word is
 * "AI"**; the mark's tooltip names QueVee. The mark loops (`thinking`) while a
 * note is being written.
 *
 * - **One read for the page.** Every kind's latest note comes in one request
 *   (`GET /macro/notes`), held in module scope with subscribers — the
 *   `symbol-index.ts` shape — so six blocks are one fetch and a ↻ in one block
 *   updates the copy every block reads.
 * - **↻ writes a new note for everyone** (quevee; admins any time). The *As of*
 *   list picks any earlier note — a read, free, for this viewer only.
 * - **Collapsed is view state (D8)**: per viewer, kept for the page in
 *   `COLLAPSED`, never written to the workspace.
 * - A read that fails draws nothing: a section without its note is still the
 *   section, and an error line above every chart would be noise.
 */

import { useEffect, useState, type CSSProperties } from "react";
import {
  fetchMacroNote,
  fetchMacroNotes,
  refreshMacroNote,
  type MacroNote as Note,
  type MacroNotes,
  type NoteKind,
} from "../../api/macro-notes";
import { QUEVEE_TOOLTIP, QueVeeMark } from "../QueVeeMark";

// -- the page's copy -----------------------------------------------------------

let notes: MacroNotes | null | undefined;
let loading: Promise<void> | null = null;
const listeners = new Set<() => void>();

function publish(): void {
  for (const l of listeners) l();
}

function load(): void {
  if (loading) return;
  loading = fetchMacroNotes().then((n) => {
    notes = n;
    publish();
  });
}

/** Tests only: forget the page's copy. */
export function resetMacroNotes(): void {
  notes = undefined;
  loading = null;
  listeners.clear();
  COLLAPSED.clear();
}

function useNotes(): MacroNotes | null | undefined {
  const [, tick] = useState(0);
  useEffect(() => {
    const l = () => tick((n) => n + 1);
    listeners.add(l);
    load();
    return () => {
      listeners.delete(l);
    };
  }, []);
  return notes;
}

/** Which blocks this viewer has folded, for the page (view state, never stored). */
const COLLAPSED = new Set<NoteKind>();

// -- the block -----------------------------------------------------------------

export interface MacroNoteProps {
  kind: NoteKind;
}

/** `2026-11-02T22:48:00.123+00:00` → `Nov 2, 3:48 PM` in the viewer's zone. */
function when(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

export function MacroNote({ kind }: MacroNoteProps) {
  const all = useNotes();
  const [collapsed, setCollapsed] = useState(() => COLLAPSED.has(kind));
  const [picked, setPicked] = useState<Note | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (all === undefined || all === null) return null; // loading, or the read failed
  const latest = all[kind] ?? null;
  const shown = picked ?? latest;

  const toggle = () => {
    const next = !collapsed;
    if (next) COLLAPSED.add(kind);
    else COLLAPSED.delete(kind);
    setCollapsed(next);
  };

  const refresh = async () => {
    setBusy(true);
    setError(null);
    const r = await refreshMacroNote(kind);
    setBusy(false);
    if (!r.ok) {
      setError(r.error);
      return;
    }
    // The new note is everyone's latest now: re-read the kind so its As of list
    // carries it, and fall back to the answer itself if that read fails.
    const fresh = (await fetchMacroNote(kind)) ?? {
      kind,
      note: r.note,
      writtenAt: r.writtenAt,
      builtAt: r.builtAt,
      latest: true,
      history: [{ writtenAt: r.writtenAt, builtAt: r.builtAt }, ...(latest?.history ?? [])],
    };
    notes = { ...(notes ?? {}), [kind]: fresh };
    setPicked(null);
    publish();
  };

  const pick = async (at: string) => {
    if (!latest || at === latest.writtenAt) {
      setPicked(null);
      return;
    }
    const got = await fetchMacroNote(kind, at);
    if (got) setPicked(got);
  };

  const asOf = shown?.note.asOf ?? shown?.writtenAt.slice(0, 10);
  const history = latest?.history ?? [];
  return (
    <div data-macro-note={kind} style={BLOCK}>
      <div style={HEAD}>
        <button
          type="button"
          onClick={toggle}
          aria-expanded={!collapsed}
          aria-label={collapsed ? "Show the AI note" : "Hide the AI note"}
          style={FOLD}
        >
          {collapsed ? "▸" : "▾"}
        </button>
        <QueVeeMark size={15} title={QUEVEE_TOOLTIP} thinking={busy} playOnClick />
        <span style={STAMP}>
          AI{kind === "overall" ? " · across the sections" : ""}
          {shown ? ` · as of ${asOf}` : " · no note yet"}
        </span>
        {history.length > 1 && (
          <select
            aria-label="As of"
            value={shown?.writtenAt ?? ""}
            onChange={(e) => void pick(e.target.value)}
            style={SELECT}
          >
            {history.map((h, i) => (
              <option key={h.writtenAt} value={h.writtenAt}>
                {when(h.writtenAt)}
                {i === 0 ? " (latest)" : ""}
              </option>
            ))}
          </select>
        )}
        <button
          type="button"
          onClick={() => void refresh()}
          disabled={busy}
          aria-label="Write a new AI note"
          title="Write a new note from the latest numbers, for everyone"
          style={REFRESH}
        >
          ↻
        </button>
      </div>
      {!collapsed && (
        <div style={BODY}>
          {busy && !shown && <p style={MUTED}>Writing…</p>}
          {shown ? (
            <>
              <p style={TEXT}>{shown.note.text}</p>
              {shown.note.lastTime && <p style={LAST}>{shown.note.lastTime}</p>}
            </>
          ) : (
            !busy && <p style={MUTED}>No note for this section yet.</p>
          )}
          {error && (
            <p role="alert" style={ERROR}>
              {error}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

const BLOCK: CSSProperties = {
  margin: "4px 8px 2px",
  padding: "4px 8px",
  borderLeft: "2px solid var(--qf-border)",
  background: "var(--qf-surface-alt)",
  borderRadius: 4,
  fontSize: 12.5,
  lineHeight: 1.45,
};
const HEAD: CSSProperties = { display: "flex", alignItems: "center", gap: 6, minHeight: 20 };
const FOLD: CSSProperties = {
  border: "none",
  background: "none",
  color: "var(--qf-text-muted)",
  cursor: "pointer",
  padding: 0,
  width: 14,
  fontSize: 11,
};
const STAMP: CSSProperties = { color: "var(--qf-text-secondary)", fontSize: 11.5 };
const SELECT: CSSProperties = { fontSize: 11, maxWidth: 170, background: "var(--qf-surface)", color: "var(--qf-text)" };
const REFRESH: CSSProperties = {
  border: "none",
  background: "none",
  color: "var(--qf-text-secondary)",
  cursor: "pointer",
  padding: "0 2px",
  fontSize: 13,
};
const BODY: CSSProperties = { paddingLeft: 20 };
const TEXT: CSSProperties = { margin: "2px 0", color: "var(--qf-text)" };
const LAST: CSSProperties = { margin: "2px 0", color: "var(--qf-text-secondary)", fontStyle: "italic" };
const MUTED: CSSProperties = { margin: "2px 0", color: "var(--qf-text-muted)" };
const ERROR: CSSProperties = { margin: "2px 0", color: "var(--qf-warn)" };
